/* =========================================================
	COUNTUP.JS INTEGRATION FOR STATS COUNTER EFFECT
========================================================= */
import { CountUp } from 'countup.js';

class AnnualReportCounter {
   constructor() {
      this.animated = new Set();
      this.respectsReducedMotion = window.matchMedia(
         '(prefers-reduced-motion: reduce)'
      ).matches;
      this.init();
   }

   init() {
      // Only initialize if we have stat items
      const statContainers = document.querySelectorAll('.stat-item');
      if (statContainers.length === 0) return;

      // Create intersection observer
      this.observer = new IntersectionObserver(
         (entries) => this.handleIntersection(entries),
         {
            threshold: 0.3,
            rootMargin: '0px 0px -100px 0px',
         }
      );

      // Find parent containers of stat items (sections containing multiple stats)
      const parentContainers = this.getParentContainers(statContainers);
      parentContainers.forEach((container) => {
         this.observer.observe(container);
      });
   }

   getParentContainers(statItems) {
      const containers = new Set();
      statItems.forEach((item) => {
         // Find the closest parent that contains multiple stat items or use the item itself
         let parent = item.parentElement;

         // Look for a container with multiple stat items (like your stats sections)
         while (parent && parent !== document.body) {
            const statsInParent = parent.querySelectorAll('.stat-item').length;
            if (statsInParent >= 2) {
               containers.add(parent);
               break;
            }
            parent = parent.parentElement;
         }

         // Fallback: if no multi-stat parent found, observe the item itself
         if (!parent || parent === document.body) {
            containers.add(item);
         }
      });
      return Array.from(containers);
   }

   handleIntersection(entries) {
      entries.forEach((entry) => {
         if (entry.isIntersecting && !this.animated.has(entry.target)) {
            this.animateStats(entry.target);
            this.animated.add(entry.target);
         }
      });
   }

   animateStats(container) {
      // Find all stat items within this container
      const statItems = container.classList.contains('stat-item')
         ? [container]
         : container.querySelectorAll('.stat-item');

      statItems.forEach((item, index) => {
         if (this.respectsReducedMotion) {
            // No animation, just ensure final values are visible
            item.classList.add('animate');
            return;
         }

         // Add CSS animation class with stagger
         setTimeout(() => {
            item.classList.add('animate');
         }, index * 200);

         // Start count-up animation
         setTimeout(() => {
            this.animateNumber(item);
         }, index * 200 + 300);
      });
   }

   animateNumber(item) {
      const numberElement = item.querySelector('span[aria-live], .stat-number');
      if (!numberElement) return;

      const targetValue = parseInt(item.dataset.countup);
      if (isNaN(targetValue)) {
         console.warn('Invalid countup value:', item.dataset.countup);
         return;
      }

      // Store original content for fallback
      const originalContent = numberElement.textContent;

      // Extract prefix and suffix from current content
      const prefix = this.extractPrefix(originalContent);
      const suffix = this.extractSuffix(originalContent, targetValue);

      // CountUp options
      const options = {
         startVal: 0,
         duration: 2.5,
         useEasing: true,
         useGrouping: true,
         separator: ',',
         decimal: '.',
         prefix: prefix,
         suffix: suffix,
      };

      // Create and start CountUp
      const countUp = new CountUp(numberElement, targetValue, options);

      if (!countUp.error) {
         countUp.start(() => {
            // Animation complete callback
            console.log(
               `Counter animation completed for value: ${targetValue}`
            );
         });
      } else {
         console.warn('CountUp error:', countUp.error);
         // Fallback: ensure properly formatted final value is shown
         numberElement.textContent = `${prefix}${targetValue.toLocaleString()}${suffix}`;
      }
   }

   extractPrefix(text) {
      // Check for dollar sign or other currency symbols at start
      const prefixMatch = text.match(/^[\$£€¥]/);
      return prefixMatch ? prefixMatch[0] : '';
   }

   extractSuffix(text, targetValue) {
      // Remove prefix first
      const withoutPrefix = text.replace(/^[\$£€¥]/, '');

      // Create regex to match the number (with or without commas)
      const numberRegex = new RegExp(
         targetValue.toLocaleString().replace(/,/g, ',?'),
         'g'
      );

      // Remove the number and what's left is the suffix
      const suffix = withoutPrefix.replace(numberRegex, '').trim();
      return suffix;
   }

   // Public method to manually trigger animation (useful for testing or late-loaded content)
   triggerAnimation(container) {
      if (container && !this.animated.has(container)) {
         this.animateStats(container);
         this.animated.add(container);
      }
   }

   // Cleanup method
   destroy() {
      if (this.observer) {
         this.observer.disconnect();
      }
      this.animated.clear();
   }
}

// Export the class
export default AnnualReportCounter;
